import React from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Button,
  Text,
  Divider,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Open16Regular,
  ArrowRight20Regular,
} from '@fluentui/react-icons';
import OrderStatusPill from './OrderStatusPill';
import PaymentStatusPill from './PaymentStatusPill';
import { ProjectPhase, isFinalPickup } from '../services/projectService';

// Read-only summary of a single phase's order, shown in an overlay drawer so the
// project work surface stays mounted underneath (no context loss). All edits live
// behind "Open full order", which navigates to the full /orders/:id editor.

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '—';
  // Local-time construction keeps the calendar day stable east of UTC (mirrors
  // ProjectDetails' isoToDate).
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

const useStyles = makeStyles({
  header: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  headTitle: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '20px',
    ...shorthands.padding('16px', '0'),
  },
  pillRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flexWrap: 'wrap',
  },
  dates: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '12px',
    flexWrap: 'wrap',
  },
  dateColumn: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    minWidth: '140px',
  },
  arrowWrap: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
  },
  facts: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
  factRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    columnGap: '16px',
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  note: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  footer: {
    marginTop: 'auto',
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('16px', '0', '0'),
  },
  openButton: {
    width: '100%',
  },
});

interface PhaseOrderDrawerProps {
  phase: ProjectPhase | null;
  open: boolean;
  onClose: () => void;
  /** Navigate to the full order editor for the given order id. */
  onOpenFullOrder: (orderId: string) => void;
}

const PhaseOrderDrawer: React.FC<PhaseOrderDrawerProps> = ({
  phase,
  open,
  onClose,
  onOpenFullOrder,
}) => {
  const styles = useStyles();
  const promoted = !!phase?.booqable_id;

  return (
    <Drawer
      type="overlay"
      separator
      position="end"
      size="small"
      open={open}
      onOpenChange={(_, { open: isOpen }) => {
        if (!isOpen) onClose();
      }}
      modalType="non-modal"
    >
      <DrawerHeader className={styles.header}>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={onClose}
            />
          }
        >
          <span className={styles.headTitle}>
            <Text weight="semibold" size={400}>
              {phase?.phaseDisplayId || (phase ? `Phase ${phase.phaseNumber}` : 'Phase')}
            </Text>
            {phase?.phaseLabel && (
              <Text className={styles.label}>{phase.phaseLabel}</Text>
            )}
          </span>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {phase && (
          <div className={styles.body}>
            <div className={styles.pillRow}>
              <OrderStatusPill status={phase.booqableStatus} />
              {promoted && phase.paymentStatus && (
                <PaymentStatusPill status={phase.paymentStatus} />
              )}
            </div>

            {isFinalPickup(phase) ? (
              // Pickup-only: no bin is delivered, so show just the planned pickup
              // date (the phase's start === the swap hinge where the last bin is
              // collected), not a delivery→pickup window.
              <div className={styles.dates}>
                <div className={styles.dateColumn}>
                  <Text className={styles.label}>Pickup</Text>
                  <Text>{formatDate(phase.startDate)}</Text>
                </div>
              </div>
            ) : (
              <div className={styles.dates}>
                <div className={styles.dateColumn}>
                  <Text className={styles.label}>Delivery</Text>
                  <Text>{formatDate(phase.startDate)}</Text>
                </div>
                <div className={styles.arrowWrap}>
                  <ArrowRight20Regular />
                </div>
                <div className={styles.dateColumn}>
                  <Text className={styles.label}>Pickup</Text>
                  <Text>{formatDate(phase.endDate)}</Text>
                </div>
              </div>
            )}

            <Divider />

            <div className={styles.facts}>
              {promoted && phase.orderNumber && (
                <div className={styles.factRow}>
                  <Text className={styles.label}>Order number</Text>
                  <Text>{phase.orderNumber}</Text>
                </div>
              )}
              <div className={styles.factRow}>
                <Text className={styles.label}>Items</Text>
                <Text>{phase.itemCount}</Text>
              </div>
              <div className={styles.factRow}>
                <Text className={styles.label}>Order total</Text>
                <Text weight="semibold">{money(phase.price)}</Text>
              </div>
              <div className={styles.factRow}>
                <Text className={styles.label}>Collected</Text>
                <Text>{money(phase.paidAmount)}</Text>
              </div>
            </div>

            {!promoted && (
              <Text className={styles.note}>
                Not yet promoted — add lines &amp; invoice in the full order.
              </Text>
            )}

            <div className={styles.footer}>
              <Button
                appearance="primary"
                icon={<Open16Regular />}
                className={styles.openButton}
                onClick={() => onOpenFullOrder(phase.id)}
              >
                Open full order
              </Button>
            </div>
          </div>
        )}
      </DrawerBody>
    </Drawer>
  );
};

export default PhaseOrderDrawer;
